'use client'

import { usePostHog } from 'posthog-js/react'
import { useEffect } from 'react'

export function useErrorTracking() {
  const posthog = usePostHog()

  useEffect(() => {
    if (!posthog) return

    // Track JavaScript errors
    const handleError = (event: ErrorEvent) => {
      posthog.capture('javascript_error', {
        error_message: event.error?.message || event.message,
        error_stack: event.error?.stack,
        filename: event.filename,
        line_number: event.lineno,
        column_number: event.colno,
        user_agent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      })
    }

    // Track unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      posthog.capture('unhandled_promise_rejection', {
        reason: event.reason?.toString() || 'Unknown rejection',
        error_stack: event.reason?.stack,
        user_agent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      })
    }

    // Add event listeners
    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    // Cleanup
    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [posthog])

  // Helper function to manually track errors
  const trackError = (error: Error, context?: Record<string, unknown>) => {
    if (!posthog) return

    posthog.capture('manual_error_tracking', {
      error_name: error.name,
      error_message: error.message,
      error_stack: error.stack,
      context: context || {},
      user_agent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString()
    })
  }

  return { trackError }
}